import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  RadioButton,
  DataTableSkeleton,
  ComposedModal,
  ModalBody,
  ModalHeader,
  DefinitionTooltip,
  ModalFooter,
  InlineLoading,
} from '@carbon/react';
import { DestroyConfirmation } from 'components/destroy-confirmation/destroy-confirmation';
import ErrorToast from 'components/error-toast/error-toast';
import { Document, Launch, TrashCan } from '@carbon/react/icons';
import { useState, useEffect } from 'react';
import styles from '../allocation-manager.module.scss';
import { Link } from 'components/link';
import { useAtom, useSetAtom } from 'jotai';
import { useSupportingFilesAtom } from 'components/well-page-content/atoms';
import { RelatedWellFilesDeprecationNotice } from 'components/well-page-content/related-well-files-deprecation-notice';
import { WellsResponse, WellSupportingFileResponse } from 'types/api-responses';
import { useUpdateAllocationManagerAtom } from 'pages/development-areas/show/atoms';
import {
  showDocumentDeleteFailedMessage,
  isTiffFileName,
  isPdfFileName,
  findSelectedSupportingFile,
} from '../utils';
import TiffViewer from './allocation-manager-tiff-viewer';

interface Props {
  allocationManagerId: number;
  hasSourceDocument: boolean;
  onAssignDocument?: (isRemove: boolean) => Promise<void>;
  supportingFilesHref: string;
  wellData: WellsResponse;
  allocationManager?: {
    version?: string;
    supportingFile?: {
      id: number;
      attachmentHref: string;
      createdAt: string;
      updatedAt: string;
    };
  };
  selectedVersion: string;
  latestAllocationManagerVersion: string | null;
  viewOnly?: boolean;
}

export const AllocationManagerSourceDocumentModal = ({
  allocationManagerId,
  hasSourceDocument,
  onAssignDocument,
  supportingFilesHref,
  wellData,
  allocationManager,
  selectedVersion,
  latestAllocationManagerVersion,
  viewOnly = false,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    hasSourceDocument && allocationManager?.supportingFile?.id
      ? allocationManager.supportingFile.id.toString()
      : null
  );
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const [pdfLoadChecking, setPdfLoadChecking] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const isAssignedToDifferentVersion = (
    file: WellSupportingFileResponse
  ): boolean => {
    const isAssignedToOtherVersion =
      allocationManager?.supportingFile?.id === file.id && hasSourceDocument;
    return isAssignedToOtherVersion;
  };

  const supportingFilesAtom = useSupportingFilesAtom(supportingFilesHref);
  const [supportingFiles] = useAtom(supportingFilesAtom);
  const supportingFilesData =
    supportingFiles.data && 'error' in supportingFiles.data
      ? []
      : supportingFiles.data;
  const updateAllocationManager = useSetAtom(useUpdateAllocationManagerAtom());

  const handleClose = () => {
    setIsModalOpen(false);
    setRemoveError(null);
  };

  const handleSubmit = async () => {
    if (!hasSourceDocument && selectedDocumentId) {
      try {
        await updateAllocationManager({
          type: 'PATCH',
          url: `/allocation_managers/${allocationManagerId}`,
          data: { supportingFileId: selectedDocumentId },
          onSuccess: async () => {
            await onAssignDocument?.(false);
            handleClose();
          },
          onError: () => {
            console.error('Error assigning source document');
          },
        });
      } catch (error) {
        console.error('Error assigning source document:', error);
      }
    } else {
      handleClose();
    }
  };

  const renderTableContent = () => {
    if (!supportingFilesData) {
      return (
        <DataTableSkeleton
          showHeader={false}
          showToolbar={false}
          columnCount={4}
        />
      );
    }

    if ('error' in supportingFilesData) {
      return <div>Error loading files</div>;
    }

    return (
      <TableContainer>
        <Table size="md">
          <TableHead>
            <TableRow>
              <TableHeader></TableHeader>
              <TableHeader>Document Name</TableHeader>
              <TableHeader>Owner</TableHeader>
              <TableHeader>File type</TableHeader>
              <TableHeader>Uploaded On</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {supportingFilesData.map((file: WellSupportingFileResponse) => (
              <TableRow key={file.id}>
                <TableCell>
                  {isAssignedToDifferentVersion(file) ? (
                    <DefinitionTooltip
                      definition={`This document is assigned to Allocation Manager Version ${allocationManager?.version} and cannot be selected`}
                      align="bottom-start"
                    >
                      <RadioButton
                        id={file.id.toString()}
                        labelText=""
                        value={file.id.toString()}
                        checked={false}
                        disabled
                        onChange={() => {}}
                      />
                    </DefinitionTooltip>
                  ) : (
                    <RadioButton
                      id={file.id.toString()}
                      labelText=""
                      value={file.id.toString()}
                      checked={selectedDocumentId === file.id.toString()}
                      onChange={() => {
                        setSelectedDocumentId(file.id.toString());
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Link
                    truncate
                    to={file.attachmentHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      // For TIFF files, we intercept the click to prevent auto-download
                      if (
                        ['.tiff', '.tif'].some((ext) =>
                          file.fileName.toLowerCase().endsWith(ext)
                        )
                      ) {
                        e.preventDefault();
                        window.open(file.attachmentHref, '_blank');
                      }
                    }}
                  >
                    {file.fileName}
                  </Link>
                </TableCell>
                <TableCell>{file.createdBy}</TableCell>
                <TableCell>{file.fileType}</TableCell>
                <TableCell>
                  {new Date(file.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const selectedFile = findSelectedSupportingFile(
    supportingFilesData,
    selectedDocumentId
  );

  const isTiffFile = selectedFile && isTiffFileName(selectedFile.fileName);
  const isPdfFile = selectedFile && isPdfFileName(selectedFile.fileName);

  useEffect(() => {
    if (!selectedFile?.attachmentHref || !isPdfFile) {
      setPdfLoadError(null);
      setPdfLoadChecking(false);
      return;
    }
    let isMounted = true;
    setPdfLoadChecking(true);
    setPdfLoadError(null);
    fetch(selectedFile.attachmentHref, { credentials: 'same-origin' })
      .then((res) => {
        if (!isMounted) return;
        if (!res.ok) {
          setPdfLoadError(`Failed to load document: ${res.status}`);
        } else {
          setPdfLoadError(null);
        }
        setPdfLoadChecking(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        setPdfLoadError(
          err instanceof Error ? err.message : 'Failed to load document'
        );
        setPdfLoadChecking(false);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedFile?.attachmentHref, selectedFile?.fileName, isPdfFile]);

  const handleViewSourceDocument = () => {
    setIsModalOpen(true);
  };

  // In viewOnly mode, only show button if document exists
  if (viewOnly && !hasSourceDocument) {
    return null;
  }

  const renderDocumentArea = (onFullScreenClick?: () => void) => {
    if (!selectedFile?.attachmentHref) return null;
    if (isTiffFile) {
      return (
        <TiffViewer
          documentUrl={selectedFile.attachmentHref}
          fileName={selectedFile.fileName}
          onFullScreenClick={onFullScreenClick}
        />
      );
    } else if (isPdfFile) {
      if (pdfLoadChecking) {
        return (
          <div className={styles.viewSourceDocumentBody}>
            <div className={styles.documentLoadingState}>
              <div className={styles.documentLoadingStateCenter}>
                <InlineLoading
                  description="Loading document..."
                  status="active"
                />
              </div>
            </div>
          </div>
        );
      }
      if (pdfLoadError) {
        const handleDownload = () => {
          window.open(selectedFile.attachmentHref, '_blank');
        };
        return (
          <div className={styles.viewSourceDocumentBody}>
            <div className={styles.unsupportedFormatPrompt}>
              <div className={styles.unsupportedFormatPromptText}>
                {pdfLoadError}
              </div>
              <Button kind="primary" size="md" onClick={handleDownload}>
                Download
              </Button>
            </div>
          </div>
        );
      }
      return (
        <div className={styles.viewSourceDocumentBody}>
          <iframe
            src={selectedFile.attachmentHref}
            title="Document Viewer"
            className={styles.documentIframe}
          />
        </div>
      );
    } else {
      const handleDownload = () => {
        window.open(selectedFile.attachmentHref, '_blank');
      };
      return (
        <div className={styles.viewSourceDocumentBody}>
          <div className={styles.unsupportedFormatPrompt}>
            <div className={styles.unsupportedFormatPromptText}>
              File format is not supported.
            </div>
            <Button kind="primary" size="md" onClick={handleDownload}>
              Download
            </Button>
          </div>
        </div>
      );
    }
  };

  const documentViewerPageUrl =
    selectedDocumentId !== null && wellData?.uwi
      ? `/wells/${wellData.uwi}/supporting_files/${selectedDocumentId}`
      : null;

  return (
    <>
      {removeError && (
        <div
          className={styles.errorToastWrapper}
          role="alert"
          aria-live="polite"
        >
          <ErrorToast
            message={removeError}
            onClose={() => {
              setRemoveError(null);
              return true;
            }}
          />
        </div>
      )}
      {hasSourceDocument ? (
        <Button
          size="md"
          kind="tertiary"
          tooltipPosition="bottom"
          tooltipAlignment="center"
          renderIcon={Document}
          onClick={handleViewSourceDocument}
        >
          View Source Document
        </Button>
      ) : !viewOnly && selectedVersion === latestAllocationManagerVersion ? (
        <Button
          size="md"
          kind="tertiary"
          tooltipPosition="bottom"
          tooltipAlignment="center"
          renderIcon={Document}
          onClick={() => setIsModalOpen(true)}
        >
          Assign Source Document
        </Button>
      ) : !viewOnly && latestAllocationManagerVersion !== null ? (
        <DefinitionTooltip
          definition="You can only assign a source document to the current version"
          align="bottom"
        >
          <Button
            size="md"
            kind="tertiary"
            renderIcon={Document}
            onClick={() => setIsModalOpen(true)}
            disabled={true}
          >
            Assign Source Document
          </Button>
        </DefinitionTooltip>
      ) : null}
      {hasSourceDocument ? (
        <ComposedModal
          open={isModalOpen}
          onClose={handleClose}
          size="lg"
          className={styles.viewSourceDocumentModal}
        >
          <ModalHeader
            title="View Source Document"
            label={`Allocation Manager ${selectedVersion}`}
          />
          <ModalBody className={styles.modalBody}>
            <div className={styles.viewSourceDocumentLayout}>
              <div className={styles.viewSourceDeprecationNotice}>
                <RelatedWellFilesDeprecationNotice />
              </div>
              <div className={styles.viewSourceMetadataBar}>
                <div className={styles.viewSourceMetadata}>
                  <div className={styles.viewSourceMetadataRow}>
                    <span className={styles.viewSourceMetadataLabel}>
                      Well Assigned to Unit:
                    </span>
                    <span className={styles.viewSourceMetadataValue}>
                      <Link to={`/wells/${wellData.uwi}`}>{wellData.uwi}</Link>
                      <span> - {wellData.name}</span>
                    </span>
                  </div>
                  {selectedFile && (
                    <>
                      <div className={styles.viewSourceMetadataRow}>
                        <span className={styles.viewSourceMetadataLabel}>
                          Document Name:
                        </span>
                        <span className={styles.viewSourceMetadataValue}>
                          {selectedFile.fileName}
                        </span>
                      </div>
                      <div className={styles.viewSourceMetadataRow}>
                        <span className={styles.viewSourceMetadataLabel}>
                          File Type:
                        </span>
                        <span className={styles.viewSourceMetadataValue}>
                          {selectedFile.fileType}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <div className={styles.viewSourceHeaderActions}>
                  {documentViewerPageUrl && (
                    <Button
                      kind="tertiary"
                      size="sm"
                      hasIconOnly
                      renderIcon={Launch}
                      iconDescription="Open in new tab"
                      tooltipAlignment="start"
                      tooltipPosition="left"
                      className={styles.viewSourceHeaderIconBtn}
                      onClick={() =>
                        window.open(documentViewerPageUrl!, '_blank')
                      }
                    />
                  )}
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={TrashCan}
                    iconDescription="Remove document"
                    onClick={() => setShowConfirmModal(true)}
                    className={styles.viewSourceRemoveBtn}
                  >
                    Remove Document
                  </Button>
                </div>
              </div>
              <div className={styles.viewSourceDocumentArea}>
                {renderDocumentArea()}
              </div>
            </div>
          </ModalBody>
          <DestroyConfirmation
            open={showConfirmModal}
            modalHeading="Remove Document from Allocation Manager"
            primaryButtonText="Remove"
            secondaryButtonText="Cancel"
            onRequestSubmit={async () => {
              setRemoveError(null);
              try {
                await updateAllocationManager({
                  type: 'PATCH',
                  url: `/allocation_managers/${allocationManagerId}`,
                  data: { supportingFileId: null },
                  onSuccess: async () => {
                    await onAssignDocument?.(true);
                    handleClose();
                  },
                  onError: (err: unknown) => {
                    setRemoveError(
                      showDocumentDeleteFailedMessage(
                        err,
                        'Failed to remove document.'
                      )
                    );
                  },
                });
              } catch (error) {
                setRemoveError(
                  showDocumentDeleteFailedMessage(
                    error,
                    'Failed to remove document.'
                  )
                );
              } finally {
                setShowConfirmModal(false);
              }
            }}
            onRequestClose={() => setShowConfirmModal(false)}
          >
            Are you sure you want to remove this document from Allocation
            Manager Version {allocationManager?.version}?
          </DestroyConfirmation>
        </ComposedModal>
      ) : (
        <ComposedModal
          open={isModalOpen}
          onClose={handleClose}
          size="lg"
          className={styles.sourceDocumentModal}
        >
          <ModalHeader
            title="Assign Source Document"
            label={`Allocation Manager ${selectedVersion}`}
          />
          <ModalBody>
            <div className={styles.modalContent}>
              <RelatedWellFilesDeprecationNotice />
              <div className={styles.wellAssignment}>
                <h4>Well Assigned to the Unit</h4>
                <div className={styles.wellInfo}>
                  <input type="checkbox" checked disabled />
                  <Link to={`/wells/${wellData.uwi}`}>{wellData.uwi}</Link>
                  <span> - {wellData.name}</span>
                </div>
              </div>
              <div className={styles.documentSelection}>
                <span>Select a document from the list below</span>
                {renderTableContent()}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button kind="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button kind="primary" onClick={handleSubmit}>
              Assign
            </Button>
          </ModalFooter>
        </ComposedModal>
      )}
    </>
  );
};
